import { EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { HasAccess } from '@permify/react-role';
import { Button, Col, Drawer, Image, message, Popover, Row, Spin, Tag, Upload } from "antd";
import moment from "moment";
import QRCode from "qrcode";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from "react-router-dom";
import { format } from 'rut.js';
import { useBoxContext } from '../../../context/BoxContext';
import { getCookie } from "../../../services/cookies";
import { deleteDocument, getDocuments, initializeExam, updateExamsData, updateExamState, uploadDocument } from "../../../services/exam";
import { callPatient, getAsset, removeAsset } from "../../../services/general";
import { createRxOrder } from "../../../services/rx";
import { uploadAssetController } from '../../../utils/fileUtils';
import groupedExams from '../../../utils/groupedExams.json';
import History from '../../components/History';
import countrySettings from '../countrySettings.json';
import IAResults from './IAResults';
import LoadRxOrder from './LoadRxOrder';
import StructuredResults from "./StructuredResults";
import ExamIntegration from "./ExamIntegration";
import UploadDocumentModal from './UploadDocumentModal';
import typesStyles from './utils/ExamTypes';
import LaboratoryLabel from './utils/LaboratoryLabel';
import { getActiveProviders } from '../../../services/providers';
import showOccupiedPatientModal from './OccupiedPatientModal';
import showBlockedCallModal from './BlockedCallModal';
import { handlers } from '../../../helpers/helpers';
import evaluationTypes from '../../../utils/evaluationType.json';
import ExamDetailPage from '../ExamDetailPage';
import ExamWebcamModal from './ExamWebcamModal';
import useExamPhoto from '../hooks/useExamPhoto';

const drugTestFormTypes = ['drug_test', 'drug_test_confirmation'];

const Header = ({
  examInfo: { patient, inputs },
  disableForm,
  setDrugModal,
  drugModal,
  admissionPatientExamId,
  fetchInputs,
  fetchMessage,
  setLoading,
  loading,
  admissionPatientId,
  examsTabs,
  examInfo,
  laboratoryData,
  isPermittedRole,
  isGroupedExams,
  fetchExamsTabs,
  getTranslationKey,
  enableRx,
  laboratoryModule,
  groupType,
}) => {
  const [documentsState, setDocuments] = useState([]);
  const [documentUrls, setDocumentUrls] = useState({});
  const [open, setOpen] = useState(false);
  const [reservation, setReservation] = useState(null);
  const [previousExamDrawerVisible, setPreviousExamDrawerVisible] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const isImageDocument = (name) => /\.(jpg|jpeg|png|gif|webp)$/i.test(name);
  const openDocumentModal = () => setOpen(true);
  const closeDocumentModal = () => setOpen(false)
  const navigate = useNavigate();
  const location = useLocation();
  const componentRef = useRef();
  const qrRef = useRef();
  const countryCode = getCookie("country_code");
  const branchOfficeId = getCookie("branch_office_id");
  const { t } = useTranslation();
  const [isRxExam, setIsRxExam] = useState({
    loading: false,
    status: null,
    showModal: false,
  });
  const [laboratoryProviders, setLaboratoryProviders] = useState(null);
  const [optometryProviders, setOptometryProviders] = useState(null);
  const [patientIdCard, setPatientIdCard] = useState();

  const historyParams = {
    data: { type: 'exams_procedures', id: admissionPatientExamId },
    types: ['admission_patient_exams',
      'admission_procedure_results',
      'admission_measurement_results',
      'admission_question_results',
    ],
  };

  useEffect(() => {
    const providers = getActiveProviders();
    const branchId = examInfo?.patient?.branch_office_id;
    if (providers && branchId) {
      const laboratoryProvidersArray = providers[branchId]?.laboratory || [];
      const hasBupaOrBionet = laboratoryProvidersArray.includes('bupa') || laboratoryProvidersArray.includes('bionet') || laboratoryProvidersArray.includes('sofsalud');
      setLaboratoryProviders(hasBupaOrBionet ? laboratoryProvidersArray : null);
    } else {
      setLaboratoryProviders(null);
    }
  }, [examInfo?.patient?.branch_office_id]);

  useEffect(() => {
    const providers = getActiveProviders();
    const branchId = examInfo?.patient?.branch_office_id;
    const hasBiartic = providers?.[branchId]?.optometry?.includes('biartic');
    setOptometryProviders(hasBiartic ? true : null);
  }, [examInfo?.patient?.branch_office_id]);

  const fetchDocuments = useCallback(async () => {
    setLoading(state => ({ ...state, documents: true }));
    try {
      const documents = await getDocuments({ admissionPatientExamId, type: 'exams' });
      setDocuments(documents);
      const imageDocuments = documents.filter(doc => isImageDocument(doc.document_name));
      const entries = await Promise.all(
        imageDocuments.map(async (doc) => {
          const result = await getAsset({ field: 'document', id: doc.id, type: doc.type });
          return [doc.id, result.document_url];
        })
      );
      setDocumentUrls(Object.fromEntries(entries));
    } catch (error) {
      console.error(error);
      message.error({ content: 'Error al obtener los documentos', style: { marginTop: '45vh', }, })
    }
    setLoading(state => ({ ...state, documents: false }));

  }, [admissionPatientExamId]);

  useEffect(() => {
    if (admissionPatientExamId) {
      fetchDocuments();
      fetchPatientIdCard();
    }
  }, [admissionPatientExamId]);

  useEffect(() => {
    const canvas = qrRef.current;
    if (!drugModal && canvas && !disableForm) {
      QRCode.toCanvas(
        canvas,
        `${admissionPatientExamId}\n${patient.patient_name}\n${moment().format('HH:mm:ss')}`,
        { width: 300 },
      ).then(() => setQrDataUrl(canvas.toDataURL()));
    }
  }, [admissionPatientExamId, disableForm, patient.patient_name, drugModal])

  const returnPage = () => {
    navigate(-1);
  };

  const handleInitializeAllExams = async () => {
    setLoading(loading => ({ ...loading, init: true }));
    if (Object.keys(groupedExams).includes(patient.exam_type) || isGroupedExams) {
      try {
        const promises = examsTabs.filter(
          exam => exam.id.toString() !== `${admissionPatientId}?groupType=${groupType}`
            && ['created', 'reevaluate'].includes(exam.state)
        ).map(async (exam) => {
          await initializeExam({ admissionPatientExamId: exam.id });
        });

        await Promise.all(promises);
        fetchExamsTabs({
          admission_patient_id: admissionPatientId,
        });
        if (groupType === 'laboratory') {
          setDrugModal(true);
        }
      } catch (error) {
        fetchMessage('error', 'Error al inicializar los exámenes');
        console.error('Error, ', error);
      } finally {
        setLoading(loading => ({ ...loading, init: false }));
      }
    }
  }

  const sendRxOrder = async () => {
    try {
      setIsRxExam(isRxExam => ({ ...isRxExam, loading: true, showModal: true }));

      const rxType = examInfo.inputs.code || 'TEXT';
      const response = await createRxOrder({ admissionPatientExamId, rxType });

      if (response?.status === 'OK' && response?.code === 200) {
        setIsRxExam(isRxExam => ({
          ...isRxExam,
          status: 'success',
          loading: false,
        }));
      } else if (response?.status === 'OK' && response?.code === 201) {
        setIsRxExam(isRxExam => ({
          ...isRxExam,
          status: 'exists',
          loading: false,
        }));
      }
    } catch (error) {
      console.error('Error, ', error);
      setIsRxExam(isRxExam => ({
        ...isRxExam,
        status: 'failed',
        loading: false,
      }));
      throw error;
    }
  };

  const handleInitializeExam = async (options) => {
    setLoading(loading => ({ ...loading, init: true }));
    try {
      if (
        !options?.manually
        && examInfo?.inputs?.form_type === 'rx'
        && enableRx
      ) {
        await sendRxOrder();
      }

      await initializeExam({ admissionPatientExamId });
      fetchInputs();
      setDrugModal(true);
    } catch (error) {
      fetchMessage('error', 'Error al inicializar el examen');
      console.error('Error, ', error);
    } finally {
      setLoading(loading => ({ ...loading, init: false }));
    }
  }

  const handleFreeAllPatients = async () => {
    setLoading(loading => ({ ...loading, free: true }));

    if (Object.keys(groupedExams).includes(patient.exam_type) || isGroupedExams) {
      try {
        const promises = examsTabs.filter(exam =>
          exam.id.toString() !== `${admissionPatientId}?groupType=${groupType}`
          && exam.state === 'occupied'
        ).map(async (exam) => {
          await updateExamState({
            admissionPatientExamId: exam.id,
            exam_state: 'created',
          });
        });
        await Promise.all(promises);
        fetchExamsTabs({
          admission_patient_id: admissionPatientId,
        });
        if (groupType === 'laboratory') {
          setDrugModal(false);
        }
      } catch (error) {
        fetchMessage('error', 'Error al liberar los pacientes');
        console.error('Error, ', error);
      } finally {
        setLoading(loading => ({ ...loading, free: false }));
      }
    }
  }

  const handleFreePatient = async () => {
    setLoading(loading => ({ ...loading, free: true }));
    try {
      await updateExamState({
        admissionPatientExamId,
        exam_state: patient.reevaluate ? 'reevaluate' : 'created',
      });
      fetchInputs();
    } catch (error) {
      fetchMessage('error', 'Error al liberar al paciente');
      console.error('Error, ', error);
    } finally {
      setLoading(loading => ({ ...loading, free: false }));
    }
  }

  const handleAddDocument = async (file, add_to_report = false) => {
    setLoading(loading => ({ ...loading, documents: true }));
    try {
      const response = await uploadDocument({
        admissionPatientExamId: admissionPatientExamId || examInfo.exams[0].id,
        body: {
          patient_id: patient.patient_id,
          document_type: groupType ? groupType : null,
          add_to_report: add_to_report,
        }
      });

      const fileResponse = await uploadAssetController({
        file: file,
        object_field: 'document',
        object_type: 'ExamsDocument',
        object_id: response.id,
      });

      if (response?.id && fileResponse) {
        await fetchDocuments();
      } else {
        fetchMessage('error', 'Documento cargado con error');
      }
    } catch (error) {
      console.error(error);
      fetchMessage('error', error.toString());
    } finally {
      setLoading(loading => ({ ...loading, documents: false }));
    }
  };

  const examPhoto = useExamPhoto({ onSave: handleAddDocument });

  const handleRemoveDocument = async (document_id) => {
    setLoading(state => ({ ...state, documents: true }));
    if (isPermittedRole) {
      try {
        const removeResult = await removeAsset({
          field: 'document',
          id: document_id,
          type: 'ExamsDocument',
        });
        const deleteResult = await deleteDocument({ examsDocumentId: document_id });
        if (removeResult?.success && deleteResult) {
          fetchMessage('success', 'Documento eliminado correctamente')
        }
        await fetchDocuments();
      } catch (error) {
        console.error('Error', error);
        fetchMessage('error', 'Error al eliminar documento')
      }
    }
    setLoading(state => ({ ...state, documents: false }));
  }

  const handleDownloadDocument = async (examDocument) => {
    setLoading(loading => ({ ...loading, documents: true }));

    try {
      const url = await getAsset({
        field: 'document',
        id: examDocument.id,
        type: examDocument.type,
      });
      const link = document.createElement("a");
      link.href = url.document_url;
      link.download = examDocument.document_name;
      link.setAttribute('target', '_blank');
      link.click();
    } catch (error) {
      fetchMessage('error', 'Error al descargar el documento');
      console.error('Error', error);
    } finally {
      setLoading(loading => ({ ...loading, documents: false }));
    }
  };

  const isUpdateButtonEnabled = () => {
    return countrySettings.details.updateButtonEnabled[countryCode] && examInfo?.exams?.some(exam => exam?.inputs?.state === "done");
  };

  const handleUpdateExamsData = async () => {
    setLoading(loading => ({ ...loading, update: true }));
    try {
      const params = {
        admissionPatientId, countryCode
      }
      const response = await updateExamsData(params);
      if (response.StatusCode == 200) {
        fetchMessage('success', 'Datos actualizados correctamente')
        fetchInputs();
      }
    } catch (error) {
      fetchMessage('error', 'Error al actualizar los datos');
      console.error(error)
    } finally {
      setLoading(loading => ({ ...loading, update: false }));
    }
  };

  const handleCallPatient = async (forceOccupiedCall = false, forceRecentCall = false) => {
    const box = getCookie('box_id');
    if (!box || typeof box !== 'string' || box.trim() === '') {
      message.warning(t('examPage.examTabs.callPatientBoxWarning'));
      setLoading((loading) => ({ ...loading, call: false }));
      return;
    }

    try {
      setLoading((loading) => ({ ...loading, call: true }));
      await callPatient({
        admissionPatientId,
        box,
        forceOccupiedCall,
        forceRecentCall,
      });
      message.success(t('examPage.examTabs.callPatientSuccess'));
    } catch (error) {
      const errorMessage = typeof error === 'string' ? error : error?.message || error?.toString?.() || 'Error';

      if (errorMessage?.includes('PATIENT_OCCUPIED_IN_EXAM') && !forceOccupiedCall) {
        setLoading((loading) => ({ ...loading, call: false }));
        showOccupiedPatientModal(errorMessage, () => handleCallPatient(true, false), t);
        return;
      }

      if (errorMessage?.includes('PATIENT_RECENTLY_CALLED') && !forceRecentCall) {
        setLoading((loading) => ({ ...loading, call: false }));
        showBlockedCallModal(errorMessage, () => handleCallPatient(forceOccupiedCall, true), t);
        return;
      }

      message.error(errorMessage || t('examPage.examTabs.callPatientError'));
      console.error('Error handleCallPatient:', error);
    } finally {
      setLoading((loading) => ({ ...loading, call: false }));
    }
  };

  const isToday = examInfo.patient?.admission_date ? moment(examInfo.patient.admission_date, 'YYYY-MM-DD').isSame(moment(), 'day') : false;
  const { hasBoxes } = useBoxContext();

  const fetchPatientIdCard = async () => {
    try {
      setLoading((loading) => ({ ...loading, idCard: true }));

      const response = await getAsset({
        id: patient.patient_id,
        type: 'Patient',
        field: 'document'
      });

      setPatientIdCard(response.document_url);
    } catch (error) {
      setPatientIdCard()
      console.error(error);
      message.error({
        content: 'Error al obtener la imagen del paciente',
        style: {
          marginTop: '35vh'
        }
      });
    } finally {
      setLoading((loading) => ({ ...loading, idCard: false }));
    }
  };


  const idCardTemplate = () => {
    let result;
    if (patientIdCard && !patientIdCard.includes('missing.png')) {
      result = (
        <Image
          src={patientIdCard}
          alt={`Documento de identidad del ${t('medicalConsultations.header.patientTitle').toLocaleLowerCase()}`}
        />
      )
    } else {
      result = (
        <Image
          src={'data:image/png;base64,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'}
          alt="Documento de identidad del paciente"
        />
      )
    }
    return result;
  }

  const headerFields = {
    full_name: {
      label: t('examPage.header.patient'),
      value: patient?.patient_name
    },
    age: {
      label: 'Edad',
      value: patient?.age
    },
    birthdate: {
      label: 'Fecha de nacimiento',
      value: patient?.birthdate ? moment(patient.birthdate).format('DD/MM/YYYY') : null
    },
    document: {
      label: handlers.documentType(patient?.document_type?.toLowerCase()),
      value: patient?.document_type?.toLowerCase() === "rut" ? format(patient?.document_number) : patient?.document_number
    },
    profession: {
      label: t('examPage.header.profession'),
      value: patient?.position || patient?.profession
    },
    company: {
      label: t('examPage.header.company'),
      value: patient?.business_name
    },
    reservation: {
      label: t('examPage.header.reservationNumber'),
      value: patient?.reservation_number
    },
    evaluation_type: {
      label: 'Tipo de evaluación',
      value: (patient?.evaluation_type || patient?.evaluation_type === 0) ? (evaluationTypes[countryCode]?.find(type => type.value.toString() === patient?.evaluation_type.toString())?.text || patient?.evaluation_type) : null
    },
    outsourcing: {
      label: 'Contrata',
      value: patient?.outsourcing_company
    },
    third_company: {
      label: 'Subcontrata',
      value: patient?.third_company
    },
    project: {
      label: 'Proyecto',
      value: patient?.project
    },
    external_id: {
      label: 'ID Externo',
      value: patient?.external_id
    },
    area: {
      label: 'Área',
      value: patient?.area
    },
    educational_lvl: {
      label: t('examPage.header.educationalLvl'),
      value: patient?.educational_lvl
    },
    id_card: {
      isTemplate: true,
      content: (
        <div className="patient-avatar-wrapper">
          {loading.idCard ? (
            <Spin className="cmt-spin custom-spinner" />
          ) : (
            idCardTemplate()
          )}
        </div>
      )
    }
  };

  const headerLayouts = {
    DEFAULT: {
      top: [
        { lg: 3, fields: ['id_card'] },
        { lg: 7, fields: ['full_name'] },
        { lg: 5, fields: ['document'] },
        { lg: 3, fields: ['age'] },
        { lg: 6, fields: ['birthdate'] }
      ],
      bottom: [
        { lg: 10, fields: ['company'] },
        { lg: 8, fields: ['profession'] },
        { lg: 6, fields: ['reservation'] }
      ]
    },
    PE: {
      top: [
        { lg: 3, fields: ['id_card'] },
        { lg: 6, fields: ['full_name'] },
        { lg: 4, fields: ['document'] },
        { lg: 2, fields: ['age'] },
        { lg: 4, fields: ['birthdate'] },
        { lg: 5, fields: ['educational_lvl'] }
      ],
      bottom: [
        { lg: 9, fields: ['company', 'outsourcing', 'third_company'] },
        { lg: 6, fields: ['project', 'area'] },
        { lg: 4, fields: ['reservation', 'profession'] },
        { lg: 5, fields: ['evaluation_type', 'external_id'] },
      ]
    }
  };

  const layoutKey = countrySettings.medicalConsultation?.useSpecificHeaderLayout?.[countryCode] === true ? countryCode : (countryCode === 'PE' ? 'PE' : 'DEFAULT');
  const headerLayout = headerLayouts[layoutKey] || headerLayouts.DEFAULT;

  const renderField = (key, lg, index) => {
    if (!key) return <div key={index} style={{ height: '36px' }} />;
    const field = headerFields[key];
    if (!field) return null;
    if (field.isTemplate) return <div key={index}>{field.content}</div>;
    return (
      <div key={index} className="header-field-item-stacked">
        <span className="header-field-label">{field.label}:</span>
        <strong className="header-field-value">
          {field.value || '-'}
        </strong>
      </div>
    );
  };

  return (
    <Row justify="center" className="header pt-20 pb-lg-50 pb-xs-20" gutter={[0, 50]}>
      <Col lg={21} xs={22}>
        <Row>
          <Col>
            <Button
              onClick={returnPage}
              className="cmt-button primary non-outline"
              type="primary"
            >
              <i className="icon-system-chevron-left mr-5" />
              Volver
            </Button>
          </Col>
        </Row>
      </Col>
      <Col lg={19} xs={22}>
        <Row justify="space-between" align='middle' className='mb-lg-40 mb-md-20 mb-sm-20 mb-xs-20'>
          <Col xl={20} lg={19} xs={24}>
            <h1
              className="mb-lg-0 mb-md-30 mb-sm-30 mb-xs-30 text-center"
            >
              {isGroupedExams ? `Exámenes de ${groupedExams[groupType].toLowerCase()}` : patient.exam_name}
            </h1>
          </Col>
          <Col xl={4} lg={4} xs={24}>
            <Row justify="end" className="exam-state-wrapper exam-state-lg-wrapper">
              <Col>
                {
                  inputs.state &&
                  <Tag className={`exam-state-text ${inputs.state}`} align="center">
                    {typesStyles[inputs.state].text}
                  </Tag>
                }
              </Col>
            </Row>
          </Col>
        </Row>
        <Row wrap={true} gutter={[20, 0]} className="header-top-row" align="middle">
          {headerLayout.top.map((column, colIndex) => (
            <Col lg={column.lg} xs={24} key={`top-${colIndex}`}>
              {column.fields.map((fieldKey, fieldIndex) => renderField(fieldKey, column.lg, fieldIndex))}
            </Col>
          ))}
        </Row>
        
        <Row wrap={true} gutter={[20, 0]} className="mt-15">
          {headerLayout.bottom.map((column, colIndex) => (
            <Col lg={column.lg} xs={24} key={`bottom-${colIndex}`}>
              {column.fields.map((fieldKey, fieldIndex) => renderField(fieldKey, column.lg, fieldIndex))}
            </Col>
          ))}
        </Row>

        <div className="header-divider" />

        <Row className="mt-20 mb-20" justify="space-between" align="middle" gutter={[20, 20]}>
          <Col>
             <Row gutter={[20, 20]} align="middle">
               <Col>
                 <div className="header-field-item-stacked pl-0">
                   <span className="header-field-label">{t('examPage.header.service')}:</span>
                   <strong className="header-field-value">
                     {countrySettings.usePrestationTitle[countryCode] ? (patient?.prestation_title || '-') : (patient?.prestation_name || '-')}
                   </strong>
                 </div>
               </Col>
               {
                 inputs.form_type === 'drug_test' && !drugModal && inputs.state === 'occupied' &&
                 <Col>
                   <canvas ref={qrRef} className="hidden" />
                   {qrDataUrl && <Image src={qrDataUrl} width={80} alt="QR" />}
                 </Col>
               }
             </Row>
          </Col>
          <Col>
             <Row gutter={[20, 20]} align="middle">
               <Col>
                 <History params={historyParams} />
               </Col>
               {hasBoxes && (
                 <Col>
                   <HasAccess
                     roles={['super_usuario', 'equipo_medico', 'equipo_psicologia', 'doctor', 'branch_office_manager', 'equipo_medico_externo']}
                     permission="call-patient"
                   >
                     <Popover
                       content={!isToday ? t('examPage.examTabs.callPatientDisabledTooltip') : ''}
                       placement="top"
                       trigger={(!isToday) ? 'hover' : 'click'}
                     >
                       <span className="full-width-inline-block">
                         <Button
                           onClick={() => handleCallPatient()}
                           className="cmt-button secondary"
                           type="primary"
                           size="medium"
                           disabled={!isToday || loading.call}
                           loading={loading.call}
                         >
                           {t('examPage.buttons.callPatient')}
                         </Button>
                       </span>
                     </Popover>
                   </HasAccess>
                 </Col>
               )}
               {
                 inputs.state !== 'done' &&
                 (inputs.state === 'occupied' || examsTabs?.filter(exam => 'occupied' === exam.state).length > 0) &&
                 <Col>
                   <HasAccess
                     roles={['super_usuario', 'equipo_medico', 'equipo_psicologia', 'equipo_medico_externo']}
                   >
                     <Button
                       onClick={isGroupedExams ? handleFreeAllPatients : handleFreePatient}
                       className="cmt-button primary outline"
                       type="primary"
                       size="medium"
                     >
                       {t('examPage.header.freePatient')}
                     </Button>
                   </HasAccess>
                 </Col>
               }
               {
                 patient.parent_admission_patient_exam_id && countrySettings.previousExamButton[countryCode] &&
                 <Col>
                   <Button
                     className="cmt-button primary outline"
                     icon={<EyeOutlined />}
                     onClick={() => setPreviousExamDrawerVisible(true)}
                   >
                     Examen anterior
                   </Button>
                 </Col>
               }
               {
                 !isGroupedExams &&
                 <Col>
                   <HasAccess
                     roles={['super_usuario', 'equipo_medico', 'equipo_psicologia', 'equipo_medico_externo']}
                   >
                     <Button
                       onClick={handleInitializeExam}
                       className="cmt-button primary"
                       type="primary"
                       size="medium"
                       disabled={!['created', 'reevaluate'].includes(inputs.state)}
                     >
                       {
                         t(`examPage.buttons.${getTranslationKey('initialize')}`)
                       }
                     </Button>
                   </HasAccess>
                 </Col>
               }
               {
                 (isGroupedExams || Object.keys(groupedExams).includes(patient.exam_type)) &&
                 <Col>
                   <HasAccess
                     roles={['super_usuario', 'equipo_medico', 'equipo_psicologia', 'equipo_medico_externo']}
                   >
                     <Button
                       onClick={handleInitializeAllExams}
                       className="cmt-button primary"
                       type="primary"
                       size="medium"
                       disabled={examsTabs?.filter(exam => ['created', 'reevaluate'].includes(exam.state)).length === 0}
                     >
                       {t(`examPage.buttons.${getTranslationKey('initialize')}`)}
                     </Button>
                   </HasAccess>
                 </Col>
               }
             </Row>
          </Col>
        </Row>

        <div className="header-divider" />
        
        <Row>
          <Col lg={18} xs={24} className='mt-15'>
            <HasAccess
              roles={['super_usuario', 'equipo_medico', 'equipo_psicologia', 'equipo_medico_externo']}
            >
              <Row>
                <p className="mb-0"><strong>Documentos cargados: {documentsState?.length === 0 && "Sin documentos"}</strong></p>
              </Row>

              {countrySettings.uploadDocumentsModal[countryCode] && (
                <Row className="documents-legend mt-20">
                  <Col className='mr-40'>
                    <Row align="middle">
                      <div className="dot internal mr-10" />
                      <p className="mb-0">{t('examPage.header.internalDocument')}</p>
                    </Row>
                  </Col>
                  <Col>
                    <Row align="middle">
                      <div className="dot report mr-10" />
                      <p className="mb-0">{t('examPage.header.reportDocument')}</p>
                    </Row>
                  </Col>
                </Row>
              )}
              {documentsState?.length > 0 && (() => {
                const nonImageDocs = documentsState.filter(doc => !isImageDocument(doc.document_name));
                const imageDocs = documentsState.filter(doc => isImageDocument(doc.document_name));
                return (
                  <>
                    {nonImageDocs.length > 0 && (
                      <Row align="middle" className="mt-20" gutter={[10, 10]}>
                        {nonImageDocs.map((document) => (
                          <Fragment key={document.id}>
                            <Col>
                              <Tag
                                className={`attachments ${document.add_to_report ? "report-document" : ""}`}
                                onClose={() => handleRemoveDocument(document.id)}
                                closable
                              >
                                <span
                                  className="label"
                                  onClick={() => { handleDownloadDocument(document) }}
                                >
                                  {document.document_name}
                                </span>
                              </Tag>
                            </Col>
                            {
                              //DEUDA TÉCNICA
                              [21, 25, 26, 31].includes(parseInt(branchOfficeId)) &&
                              examInfo.patient.exam_type === 'laboratory' && (
                                <Col>
                                  <IAResults
                                    getAsset={getAsset}
                                    document={document}
                                    examInfo={examInfo}
                                    type="lab"
                                  />
                                </Col>
                              )
                            }
                          </Fragment>
                        ))}
                      </Row>
                    )}
                    {imageDocs.length > 0 && (
                      <Row align="middle" className="mt-20" gutter={[10, 10]}>
                        {imageDocs.map((document) => {
                          const url = documentUrls[document.id];
                          if (!url) return null;
                          return (
                            <Col key={document.id}>
                              <div className={`img-attachment ${document.add_to_report ? "report-img-attachment" : ""}`}>
                                <Image src={url} alt={document.document_name} />
                                <button
                                  type="button"
                                  className="img-attachment-delete"
                                  onClick={() => handleRemoveDocument(document.id)}
                                >
                                  <span className="icon-system-delete" title="icon-system-delete" />
                                </button>
                              </div>
                            </Col>
                          );
                        })}
                      </Row>
                    )}
                  </>
                );
              })()}
            </HasAccess>
          </Col>
        </Row>
      </Col>

      <LoadRxOrder
        isRxExam={isRxExam}
        setIsRxExam={setIsRxExam}
        handleInitExam={handleInitializeExam}
      />

      <Col
        lg={19}
        xs={22}
        className="document-history-row p-0"
      >
        <Row justify="space-between" gutter={[0, 30]}>
          <Col lg={15} xs={24}>
            <Row gutter={[20, 20]}>
              <Col xl={7} lg={8} xs={24} className='add-documents-container'>
                {countrySettings.uploadDocumentsModal[countryCode] ? <HasAccess
                  roles={['super_usuario', 'equipo_medico', 'equipo_psicologia', 'equipo_medico_externo']}
                >
                  <Button
                    className="cmt-button secondary pl-15 pr-20"
                    type="primary"
                    size='medium'
                    block
                    onClick={openDocumentModal}
                    icon={<PlusOutlined />}
                  >
                    Documentos
                  </Button>
                </HasAccess> : <Upload
                  showUploadList={false}
                  beforeUpload={(file) => handleAddDocument(file)}
                  customRequest={() => console.log("")}
                >
                  <HasAccess
                    roles={['super_usuario', 'equipo_medico', 'equipo_psicologia', 'equipo_medico_externo']}
                  >
                    <Button
                      className="cmt-button secondary pl-15 pr-15"
                      type="primary"
                      size='medium'
                      block
                      icon={<PlusOutlined />}
                    >
                      Documentos
                    </Button>
                  </HasAccess>
                </Upload>}
              </Col>
              {countrySettings.takePhotoWithWebcamButton[countryCode] &&
                <Col xl={6} lg={7} xs={24}>
                  <HasAccess
                    roles={['super_usuario', 'equipo_medico', 'equipo_psicologia', 'equipo_medico_externo']}
                  >
                    <Button
                      className="cmt-button secondary pl-15 pr-15"
                      type="primary"
                      size="medium"
                      block
                      onClick={examPhoto.openModal}
                      icon={<span className="icon-camera-bold mr-10" title="icon-camera-bold" />}
                    >
                      Tomar foto
                    </Button>
                  </HasAccess>
                </Col>}
              {countrySettings.details.updateButtonEnabled[countryCode] && patient.exam_type === 'laboratory' &&
                <Popover
                  placement='top'
                  trigger={isUpdateButtonEnabled() ? '' : 'hover'}
                  title={<h3 className='mb-0'>Actualizar datos</h3>}
                  content={
                    <Row>
                      <p className='mb-0'>Esta funcionalidad se activa una vez que el examen esté tomado.</p>
                    </Row>
                  }
                  overlayClassName='update-data-popover'
                >
                  <Col xl={7} lg={8} xs={24}>
                    <Button
                      className='cmt-button secondary outline'
                      type='primary'
                      size='medium'
                      disabled={!isUpdateButtonEnabled()}
                      onClick={handleUpdateExamsData}
                    >
                      Actualizar datos
                    </Button>
                  </Col>
                </Popover>
              }
            </Row>
          </Col>

          <Col lg={4} xs={24}>
            <Row gutter={[0, 20]}>
              {(isPermittedRole && false) &&
                <Col lg={24} xs={24}>
                  <Link
                    to={`/laboratorio/${admissionPatientId}/ver?from=exams`}
                  >
                    <Button
                      className={
                        inputs.state !== 'occupied' ? 'cmt-button disabled' :
                          'cmt-button secondary'}
                      type='primary'
                      disabled={inputs.state !== 'occupied'}
                    >
                      <span className='icon-system-plus mr-10' />
                      Agregar examen
                    </Button>
                  </Link>
                </Col>
              }

              <Col lg={24} xs={24}>
                {
                  (
                    examInfo.patient.exam_type === 'laboratory'
                  )
                  && (
                    <LaboratoryLabel patient={examInfo?.patient} />
                  )
                }
                {
                  countrySettings.drugTestLabel[countryCode]
                  && drugTestFormTypes.includes(inputs.form_type)
                  && (
                    <LaboratoryLabel patient={examInfo?.patient} />
                  )
                }
              </Col>
              <Col lg={24} xs={24}>
                <Spin spinning={loading.examsTabs || loading.inputs} className="cmt-spin">
                  {
                    admissionPatientId && laboratoryProviders && examsTabs && examInfo.patient &&
                    <StructuredResults
                      laboratoryData={laboratoryData}
                      admissionPatientId={admissionPatientId}
                      branchOfficeId={examInfo?.patient?.branch_office_id}
                    />
                  }
                  {
                    admissionPatientId && optometryProviders && examInfo.patient?.exam_type === 'biartic' &&
                    <ExamIntegration
                      admissionPatientId={admissionPatientId}
                      branchOfficeId={examInfo?.patient?.branch_office_id}
                    />
                  }
                </Spin>
              </Col>
            </Row>
          </Col>
        </Row>
      </Col>
      {countrySettings.uploadDocumentsModal[countryCode] &&
        <UploadDocumentModal open={open} closeModal={closeDocumentModal} loading={loading} uploadFunction={handleAddDocument} />}
      {patient.parent_admission_patient_exam_id && countrySettings.previousExamButton[countryCode] && (
        <Drawer
          className="exam-detail-drawer"
          title="Examen anterior"
          placement="right"
          width="74%"
          open={previousExamDrawerVisible}
          onClose={() => setPreviousExamDrawerVisible(false)}
          closable={true}
          destroyOnClose
        >
          <ExamDetailPage
            from={'expert'}
            fromExternal={true}
            externalExam={{
              admission_patient_id: patient.parent_admission_patient_id,
            }}
            externalAdmissionPatientExamId={groupType ? undefined : patient.parent_admission_patient_exam_id}
            externalGroupType={groupType || undefined}
            readOnly={true}
          />
        </Drawer>
      )}
      {examPhoto.visible && <ExamWebcamModal {...examPhoto} countrySettings={countrySettings} countryCode={countryCode} />}
    </Row>
  )
}


export default Header;